import type {
  FilterSpecification,
  Map,
  SymbolLayerSpecification,
} from "maplibre-gl";
import { isPoiSourceLayer } from "../layers.ts";

export type PoiDensity = "less" | "default" | "more";

/**
 * Snapshot of a POI layer's original style values. Density adjustments are
 * always derived from this snapshot so repeated changes do not accumulate.
 */
export type PoiLayerBaseline = {
  id: string;
  zoomRange?: {
    min: number;
    max: number;
  };
  filter: unknown;
};

type FilterOverride = {
  layerId: string;
  filter: FilterSpecification;
};

/**
 * Handles provider-specific POI visibility encoded in layer filters rather
 * than zoom ranges.
 */
export type PoiDensityAdapter = (
  baselines: readonly PoiLayerBaseline[],
  zoomShift: number,
  warn: (message: string) => void,
) => FilterOverride[];

const ZOOM_SHIFT: Record<PoiDensity, number> = {
  less: 2,
  default: 0,
  more: -2,
};

/**
 * Applies a reactive density preference to MapLibre's imperative style
 * lifecycle. Style changes load asynchronously, so the requested settings
 * must be retained until `style.load` provides the new layers. Each loaded
 * style is then captured once and all adjustments derive from those originals.
 */
export class PoiDensityController {
  // Latest requested configuration, retained while its style is loading.
  #pendingStyleId: string | undefined;
  #pendingAdapter: PoiDensityAdapter | undefined;
  #density: PoiDensity = "default";

  // Identifies the style represented by the current baselines. This prevents
  // a new request from being applied to the previous style while MapLibre loads.
  #loadedStyleId: string | undefined;

  // Original values for the loaded style, kept to prevent cumulative shifts.
  #baselines: PoiLayerBaseline[] = [];

  constructor(
    private readonly map: Map,
    private readonly warn: (message: string) => void = console.warn,
  ) {
    map.on("style.load", this.handleStyleLoad);
  }

  update(
    styleId: string,
    density: PoiDensity,
    adapter?: PoiDensityAdapter,
  ): void {
    this.#pendingStyleId = styleId;
    this.#pendingAdapter = adapter;
    this.#density = density;

    if (!this.map.isStyleLoaded()) return;

    if (this.#loadedStyleId === undefined) {
      this.#loadedStyleId = styleId;
      this.captureBaseline();
    } else if (this.#loadedStyleId !== styleId) {
      return;
    }

    this.apply();
  }

  destroy(): void {
    this.map.off("style.load", this.handleStyleLoad);
  }

  private readonly handleStyleLoad = (): void => {
    if (this.#pendingStyleId === undefined) return;
    // Diplomat (and possibly other extensions in the future) localizes labels with setStyle(), which emits another style.load
    // for the same style. Keep the first baseline instead of recapturing our
    // already-adjusted layer values.
    if (this.#loadedStyleId !== this.#pendingStyleId) {
      this.#loadedStyleId = this.#pendingStyleId;
      this.captureBaseline();
    }
    this.apply();
  };

  private captureBaseline(): void {
    this.#baselines = (this.map.getStyle()?.layers ?? [])
      .filter((layer) => {
        // Density applies only to rendered POI labels/icons. Other symbol
        // layers include place names, road labels, and similar map context.
        if (layer.type !== "symbol") return false;
        const sourceLayer = layer["source-layer"];
        return sourceLayer !== undefined && isPoiSourceLayer(sourceLayer);
      })
      .map((layer) => ({
        id: layer.id,
        zoomRange: layer.minzoom !== undefined || layer.maxzoom !== undefined
          ? {
            min: layer.minzoom ?? 0,
            max: layer.maxzoom ?? 24,
          }
          : undefined,
        filter: structuredClone(
          (layer as SymbolLayerSpecification).filter,
        ),
      }));
  }

  private apply(): void {
    const zoomShift = ZOOM_SHIFT[this.#density];
    for (const baseline of this.#baselines) {
      if (!this.map.getLayer(baseline.id)) continue;
      if (!baseline.zoomRange) continue;

      this.map.setLayerZoomRange(
        baseline.id,
        Math.min(
          Math.max(0, Math.min(24, baseline.zoomRange.min + zoomShift)),
          baseline.zoomRange.max,
        ),
        baseline.zoomRange.max,
      );
    }

    const filterOverrides = this.#pendingAdapter?.(
      this.#baselines,
      zoomShift,
      this.warn,
    ) ?? [];
    for (const override of filterOverrides) {
      if (!this.map.getLayer(override.layerId)) {
        this.warn(
          `POI density adapter returned an override for missing layer "${override.layerId}".`,
        );
        continue;
      }

      this.map.setFilter(override.layerId, override.filter);
    }
  }
}
